import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, ChevronRight, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { CallBrief, type IssueHandler } from "@/components/prospects/CallBrief";
import { OutcomeModal } from "@/components/prospects/OutcomeModal";
import { CallButton } from "@/components/prospects/CallButton";
import { PointageButtons } from "@/components/prospects/PointageButtons";
import { WhatsAppButton } from "@/components/prospects/WhatsAppButton";
import { CallSummaryBadge } from "@/components/prospects/CallSummaryBadge";
import { CallsStrip } from "@/components/stats/CallsStrip";
import { useToast } from "@/components/ui/Toast";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { api } from "@/lib/api";
import { dayMonth, pickTemplate, rdvTimes, rowMeta } from "@/lib/brief";
import { TONE_STYLES } from "@/lib/colors";
import { issuePillLabel } from "@/lib/issue-pill";
import { annulerPointage, pointageState } from "@/lib/pointage";
import { cn, fullName } from "@/lib/utils";
import type { GoogleEvent, ProductKey, Prospect, RdvIssue, WebinarAttendee } from "@shared/schema";
import { SETTINGS_DEFAULTS } from "@shared/schema";
import { parseCalendlyBlock } from "@shared/calendly";
import type { OutcomeChoice } from "@shared/outcome";
import type { CallSummaryView, TodayData } from "@shared/webinar-api";
import { bookedAtOf } from "@shared/whatsapp";

type Section = "late" | "call" | "followup";

type PointOutcome = "attended" | "no_show";
type Override = { prospect: Prospect; outcome: PointOutcome | null };

/** Icônes d'action de la ligne : 32 px sur ordinateur (comme les boutons btn-sm), 44 px au toucher. */
const ICON_SM = "sm:h-8 sm:w-8";

const CHIPS: { label: string; choice: OutcomeChoice }[] = [
  { label: "Closé PC", choice: "closed_pc" },
  { label: "Closé Acc.", choice: "closed_acc" },
  { label: "À relancer", choice: "relance" },
  { label: "Perdu", choice: "lost" },
];

/**
 * Ligne d'un RDV dont l'issue est notée (closé / à relancer / perdu) : pastille, fond teinté très clair,
 * barre de 4 px à gauche. Le survol fonce un peu la teinte au lieu de revenir au blanc.
 */
const ISSUE_STYLES: Record<RdvIssue, { pill: string; row: string; hover: string }> = {
  closed: {
    pill: "bg-[var(--color-success)] text-white",
    row: "bg-[color-mix(in_srgb,var(--color-success)_6%,white)] shadow-[inset_4px_0_0_var(--color-success)]",
    hover: "hover:bg-[color-mix(in_srgb,var(--color-success)_11%,white)]",
  },
  relance: {
    pill: "bg-[var(--color-warning-soft)] text-[#b45309]",
    row: "bg-[color-mix(in_srgb,var(--color-warning)_7%,white)] shadow-[inset_4px_0_0_var(--color-warning)]",
    hover: "hover:bg-[color-mix(in_srgb,var(--color-warning)_12%,white)]",
  },
  lost: {
    // Rouge plein (comme le vert plein de « Closé ») : le rouge clair est déjà pris par « À pointer ».
    pill: "bg-[var(--color-coral-600)] text-white",
    row: "bg-[color-mix(in_srgb,var(--color-coral-600)_6%,white)] shadow-[inset_4px_0_0_var(--color-coral-600)]",
    hover: "hover:bg-[color-mix(in_srgb,var(--color-coral-600)_11%,white)]",
  },
};

interface Row {
  key: string;
  section: Section;
  prospect: Prospect;
  attendee: WebinarAttendee | null;
  event: GoogleEvent | null;
  hadAttendedRdv: boolean;
  lastSaleProduct: ProductKey | null;
  callSummary: CallSummaryView | null;
}

/** Deux colonnes (liste + fiche ouverte) à partir de cette largeur ; en dessous, un clic ouvre la page prospect. */
const WIDE_QUERY = "(min-width: 1000px)";

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function todayLabel(): string {
  return capitalize(
    new Date().toLocaleDateString("fr-FR", {
      timeZone: "Europe/Paris",
      weekday: "long",
      day: "numeric",
      month: "long",
    }),
  );
}

function SectionTitle({
  title,
  count,
  hint,
  tone,
}: {
  title: string;
  count?: number;
  hint?: string;
  tone?: "warn";
}) {
  return (
    <div className="mb-2 mt-6 flex items-baseline gap-2 first:mt-0">
      <h2 className="text-base font-semibold text-[var(--color-navy-700)]">{title}</h2>
      {count !== undefined && (
        <span
          className={cn(
            "pill",
            tone === "warn"
              ? [TONE_STYLES.yellow.pillBg, TONE_STYLES.yellow.pillFg]
              : [TONE_STYLES.navy.pillBg, TONE_STYLES.navy.pillFg],
          )}
        >
          {count}
        </span>
      )}
      {hint && <span className="text-xs text-[var(--color-muted-foreground)]">{hint}</span>}
    </div>
  );
}

/** Deuxième ligne : prochaine action d'une relance, jour d'un RDV passé sans issue, sinon rien. */
function leadText(row: Row, times: ReturnType<typeof rdvTimes>): string | null {
  const { prospect } = row;
  if (row.section === "followup") {
    if (prospect.nextAction) return prospect.nextAction;
    const day = dayMonth(prospect.nextFollowUpAt);
    return day ? `Relance prévue le ${day}` : null;
  }
  if (row.section === "late") return times ? `RDV ${times.parisDay} · ${times.local} chez toi` : null;
  return null;
}

function StatePill({ row }: { row: Row }) {
  const { event, prospect } = row;
  if (!event) return null;
  if (event.issue) {
    const label = issuePillLabel(event.issue, prospect, row.lastSaleProduct);
    return (
      <span title={label} className={cn("pill max-w-full truncate", ISSUE_STYLES[event.issue].pill)}>
        {label}
      </span>
    );
  }
  const state = pointageState(event);
  if (state === "topoint") {
    return <span className={cn("pill", TONE_STYLES.coral.pillBg, TONE_STYLES.coral.pillFg)}>À pointer</span>;
  }
  if (state === "present") {
    return <span className={cn("pill", TONE_STYLES.green.pillBg, TONE_STYLES.green.pillFg)}>Présent</span>;
  }
  if (state === "absent") {
    const day = prospect.nextAction === "Rappeler" ? dayMonth(prospect.nextFollowUpAt) : null;
    return (
      <span className={cn("pill", TONE_STYLES.slate.pillBg, TONE_STYLES.slate.pillFg)}>
        {day ? `Absent · rappel ${day}` : "Absent"}
      </span>
    );
  }
  return null;
}

function RowItem({
  row,
  wide,
  selected,
  bookingUrl,
  onPick,
  onPointed,
  onUndo,
  onIssue,
}: {
  row: Row;
  wide: boolean;
  selected: boolean;
  bookingUrl: string;
  onPick: (row: Row) => void;
  onPointed: (row: Row, prospect: Prospect, outcome: PointOutcome) => void;
  onUndo: (row: Row) => void;
  onIssue: IssueHandler;
}) {
  const toast = useToast();
  const { prospect, attendee, event } = row;
  const times = rdvTimes(event?.startTime);
  const meta = rowMeta(attendee, parseCalendlyBlock(prospect.context));
  const isSel = wide && selected;
  const lead = leadText(row, times);
  const state = event ? pointageState(event) : null;
  const timed = row.section === "call";
  const issue = event?.issue ?? null;
  const toPoint = state === "topoint" && !issue;

  async function undo(e: React.MouseEvent) {
    e.stopPropagation();
    if (!event) return;
    try {
      await annulerPointage(event.id);
      onUndo(row);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Annulation impossible");
    }
  }

  const waIcon = (template: Parameters<typeof WhatsAppButton>[0]["template"]) => (
    <WhatsAppButton
      prospect={prospect}
      template={template}
      rdvStart={event?.startTime}
      bookedAt={bookedAtOf(event?.rawJson)}
      bookingUrl={bookingUrl}
      size="sm"
      variant="icon"
      className={ICON_SM}
    />
  );

  let actions: React.ReactNode = null;
  if (!event || state === null || issue) {
    // Relance sans RDV, ou issue déjà notée (elle se change depuis la fiche) : WhatsApp + Appeler.
    actions = (
      <>
        {waIcon(pickTemplate(prospect, event, undefined, row.hadAttendedRdv))}
        <CallButton phone={prospect.phone} variant="icon" className={ICON_SM} />
      </>
    );
  } else if (state === "upcoming") {
    actions = (
      <>
        <WhatsAppButton
          prospect={prospect}
          label="Confirmer"
          template={pickTemplate(prospect, event, undefined, row.hadAttendedRdv)}
          rdvStart={event.startTime}
          bookedAt={bookedAtOf(event.rawJson)}
          bookingUrl={bookingUrl}
          size="sm"
          className="max-sm:h-11 max-sm:flex-1"
        />
        <CallButton phone={prospect.phone} variant="icon" className={ICON_SM} />
      </>
    );
  } else if (state === "topoint") {
    actions = (
      <>
        <PointageButtons
          prospectId={prospect.id}
          eventId={event.id}
          eventStart={event.startTime}
          onDone={(p, o) => onPointed(row, p, o)}
          className="max-sm:flex-1 max-sm:[&>button]:flex-1"
        />
        <CallButton phone={prospect.phone} variant="icon" className={ICON_SM} />
      </>
    );
  } else if (state === "absent") {
    actions = (
      <>
        <CallButton
          phone={prospect.phone}
          variant="solid"
          className="max-sm:h-11 max-sm:flex-1 sm:h-8 sm:px-3 sm:text-[13px]"
        />
        <WhatsAppButton
          prospect={prospect}
          label="Message absence"
          template="absence"
          rdvStart={event.startTime}
          bookedAt={bookedAtOf(event.rawJson)}
          bookingUrl={bookingUrl}
          size="sm"
          variant="solid"
          className="max-sm:h-11 max-sm:flex-1"
        />
      </>
    );
  } else {
    actions = (
      <>
        <span className={cn("gap-1.5", wide ? "flex" : "grid w-full grid-cols-2")}>
          {CHIPS.map((c) => (
            <button
              key={c.choice}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onIssue(prospect, event.id, c.choice);
              }}
              className="whitespace-nowrap rounded-full border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-3 py-1.5 text-xs font-semibold text-[var(--color-navy-700)] hover:bg-[var(--color-muted)] max-sm:min-h-11"
            >
              {c.label}
            </button>
          ))}
        </span>
        <span className="flex items-center gap-2">
          {waIcon(pickTemplate(prospect, event, undefined, row.hadAttendedRdv))}
          <CallButton phone={prospect.phone} variant="icon" className={ICON_SM} />
        </span>
      </>
    );
  }

  return (
    <li
      className={cn(
        "border-b border-[var(--color-border)] last:border-b-0",
        wide && "flex flex-wrap items-center",
        toPoint && "bg-[var(--color-coral-50)] shadow-[inset_3px_0_0_var(--color-coral-500)]",
        issue && ISSUE_STYLES[issue].row,
        isSel &&
          !toPoint &&
          !issue &&
          "bg-[var(--color-navy-50)] shadow-[inset_3px_0_0_var(--color-coral-500)]",
        // Ligne teintée (à pointer, issue notée) sélectionnée : la teinte et sa barre restent, anneau navy.
        isSel && (toPoint || issue) && "ring-2 ring-inset ring-[var(--color-navy-600)]",
      )}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => onPick(row)}
        onKeyDown={(e) => {
          if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            onPick(row);
          }
        }}
        aria-current={isSel ? "true" : undefined}
        className={cn(
          "flex min-w-0 cursor-pointer items-center gap-3 px-3 text-left",
          issue ? ISSUE_STYLES[issue].hover : "hover:bg-[var(--color-surface-2)]",
          wide ? "min-w-[260px] flex-1 py-2.5" : "w-full pb-1 pt-2.5",
        )}
      >
        {timed && (
          <span className="w-[72px] shrink-0 sm:w-[88px] whitespace-nowrap tabular">
            {times ? (
              <>
                <b className="block text-[15px] text-[var(--color-navy-700)]">{times.local}</b>
                <span className="block text-xs text-[var(--color-muted-foreground)]">
                  {times.paris} Paris
                </span>
              </>
            ) : (
              <span className="text-xs text-[var(--color-muted-foreground)]">Heure inconnue</span>
            )}
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <b className="max-w-full shrink-0 truncate text-sm text-[var(--color-navy-700)]">
              {fullName(prospect)}
            </b>
            <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-0.5">
              <StatePill row={row} />
              {(state === "present" || state === "absent") && !issue && (
                <button
                  type="button"
                  onClick={undo}
                  className="text-xs text-[var(--color-muted-foreground)] underline hover:text-[var(--color-navy-700)] max-sm:-my-3.5 max-sm:inline-flex max-sm:min-h-11 max-sm:items-center max-sm:px-1"
                >
                  annuler
                </button>
              )}
            </span>
          </span>
          {lead && (
            <span className="block truncate text-xs text-[var(--color-muted-foreground)]">
              {lead}
            </span>
          )}
          <span className="block truncate text-xs text-[var(--color-muted-foreground)]">
            {meta || "Pas de données du live"}
          </span>
          {row.callSummary && (
            <span className="mt-0.5 block">
              <CallSummaryBadge summary={row.callSummary} variant="row" />
            </span>
          )}
        </span>
        {!wide && (
          <ChevronRight size={16} className="shrink-0 text-[var(--color-muted-foreground)] max-sm:hidden" />
        )}
      </div>
      <div
        className={cn(
          "flex flex-wrap items-center gap-1.5 sm:gap-2",
          wide
            ? "ml-auto justify-end py-2 pl-3 pr-3"
            : [
                "pb-2.5 pr-3 pt-1",
                timed
                  ? "pl-[calc(0.75rem+72px+0.75rem)] sm:pl-[calc(0.75rem+88px+0.75rem)]"
                  : "pl-3",
                "max-sm:pl-3",
              ],
        )}
      >
        {actions}
      </div>
    </li>
  );
}

export function TodayPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const wide = useMediaQuery(WIDE_QUERY);
  const [data, setData] = useState<TodayData | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [bookingUrl, setBookingUrl] = useState<string>(SETTINGS_DEFAULTS.calendly_booking_url);
  const [heldAtById, setHeldAtById] = useState<Map<number, string>>(new Map());
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const [pointed, setPointed] = useState<Map<number, Override>>(new Map());

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      setData(await api.today());
      setPointed(new Map());
    } catch (e) {
      setFailed(true);
      toast.error(e instanceof Error ? e.message : "Erreur");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  // Réglage du lien et dates des lives : utiles mais pas bloquants.
  useEffect(() => {
    let active = true;
    api
      .settings()
      .then((r) => {
        const url = r.settings.calendly_booking_url;
        if (active && url) setBookingUrl(url);
      })
      .catch(() => {});
    api
      .listWebinars()
      .then((r) => {
        if (active) setHeldAtById(new Map(r.webinars.map((w) => [w.id, w.heldAt])));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const rows = useMemo(() => {
    const ov = (prospect: Prospect, event: GoogleEvent) => {
      const o = pointed.get(event.id);
      // Comme le serveur : « présent » garde l'issue notée, absent ou annulation l'efface.
      return o
        ? {
            prospect: o.prospect,
            event: { ...event, outcome: o.outcome, issue: o.outcome === "attended" ? event.issue : null },
          }
        : { prospect, event };
    };
    const late: Row[] = (data?.late ?? []).map((c) => ({
      key: `late-${c.event.id}`,
      section: "late",
      ...ov(c.prospect, c.event),
      attendee: c.attendee,
      hadAttendedRdv: c.hadAttendedRdv,
      lastSaleProduct: c.lastSaleProduct,
      callSummary: c.callSummary,
    }));
    const calls: Row[] = (data?.calls ?? []).map((c) => ({
      key: `call-${c.event.id}`,
      section: "call",
      ...ov(c.prospect, c.event),
      attendee: c.attendee,
      hadAttendedRdv: c.hadAttendedRdv,
      lastSaleProduct: c.lastSaleProduct,
      callSummary: c.callSummary,
    }));
    const followUps: Row[] = (data?.followUps ?? []).map((f) => ({
      key: `fu-${f.prospect.id}`,
      section: "followup",
      prospect: f.prospect,
      attendee: f.attendee,
      event: null,
      hadAttendedRdv: f.hadAttendedRdv,
      lastSaleProduct: f.lastSaleProduct,
      callSummary: f.callSummary,
    }));
    return { late, calls, followUps };
  }, [data, pointed]);

  // Le contenu éditable d'une fiche (notes) est gardé ici pour qu'il survive au changement de ligne.
  const [notesOverride, setNotesOverride] = useState<Map<number, string | null>>(new Map());
  const withNotes = useCallback(
    (p: Prospect): Prospect =>
      notesOverride.has(p.id) ? { ...p, briefNotes: notesOverride.get(p.id) ?? null } : p,
    [notesOverride],
  );
  const onNotesSaved = useCallback((id: number, briefNotes: string | null) => {
    setNotesOverride((prev) => new Map(prev).set(id, briefNotes));
  }, []);

  const all = useMemo(
    () => [...rows.late, ...rows.calls, ...rows.followUps],
    [rows],
  );
  const selected =
    all.find((r) => r.key === selectedKey) ??
    rows.calls[0] ??
    rows.late[0] ??
    rows.followUps[0] ??
    null;

  const [issue, setIssue] = useState<{
    prospect: Prospect;
    eventId?: number;
    choice?: OutcomeChoice;
  } | null>(null);
  const onIssue: IssueHandler = (prospect, eventId, choice) =>
    setIssue({ prospect, eventId, choice });
  const issueEventStart =
    issue?.eventId !== undefined
      ? (all.find((r) => r.event?.id === issue.eventId)?.event?.startTime ?? null)
      : null;
  // Produit de la dernière vente de la ligne ouverte : évite le GET de la modale.
  const issueLastSale = issue
    ? (all.find((r) => r.prospect.id === issue.prospect.id)?.lastSaleProduct ?? null)
    : null;

  const onPointed = useCallback((row: Row, prospect: Prospect, outcome: PointOutcome) => {
    if (!row.event) return;
    setPointed((prev) => new Map(prev).set(row.event!.id, { prospect, outcome }));
  }, []);
  const onStripPointed = useCallback(
    (eventId: number, prospect: Prospect, outcome: PointOutcome) =>
      setPointed((prev) => new Map(prev).set(eventId, { prospect, outcome })),
    [],
  );
  const onUndo = useCallback((row: Row) => {
    if (!row.event) return;
    setPointed((prev) =>
      new Map(prev).set(row.event!.id, { prospect: row.prospect, outcome: null }),
    );
  }, []);

  function pick(row: Row) {
    if (wide) setSelectedKey(row.key);
    // Le RDV de la ligne suit la navigation : la fiche et l'issue portent sur lui.
    else navigate(`/prospects/${row.prospect.id}${row.event ? `?event=${row.event.id}` : ""}`);
  }

  const followGroups = useMemo(() => {
    const after = { title: "Après un RDV", rows: rows.late };
    const hot = rows.followUps.filter((r) => r.prospect.segment === "B");
    const warm = rows.followUps.filter((r) => r.prospect.segment === "C");
    const other = rows.followUps.filter(
      (r) => r.prospect.segment !== "B" && r.prospect.segment !== "C",
    );
    return [
      after,
      { title: "Relances de chauds", rows: hot },
      { title: "Messages à envoyer", rows: warm },
      { title: "Autres relances", rows: other },
    ].filter((g) => g.rows.length > 0);
  }, [rows.late, rows.followUps]);

  const list = (
    <div className="min-w-0">
      <SectionTitle
        title="Appels"
        count={rows.calls.length}
        hint={wide ? "clique pour ouvrir la fiche" : undefined}
      />
      {rows.calls.length === 0 ? (
        <p className="surface px-4 py-5 text-sm text-[var(--color-muted-foreground)]">
          Aucun appel aujourd'hui.
        </p>
      ) : (
        <ul className="surface overflow-hidden">
          {rows.calls.map((r) => (
            <RowItem key={r.key} row={r} wide={wide} selected={selected?.key === r.key} bookingUrl={bookingUrl} onPick={pick} onPointed={onPointed} onUndo={onUndo} onIssue={onIssue} />
          ))}
        </ul>
      )}

      <SectionTitle title="Relances et messages" count={rows.late.length + rows.followUps.length} />
      {rows.late.length + rows.followUps.length === 0 ? (
        <p className="surface px-4 py-5 text-sm text-[var(--color-muted-foreground)]">
          Rien à relancer pour aujourd'hui.
        </p>
      ) : (
        followGroups.map((g) => (
          <div key={g.title} className="mb-4 last:mb-0">
            <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-[0.05em] text-[var(--color-muted-foreground)]">
              {g.title} · {g.rows.length}
            </h3>
            <ul className="surface overflow-hidden">
              {g.rows.map((r) => (
                <RowItem key={r.key} row={r} wide={wide} selected={selected?.key === r.key} bookingUrl={bookingUrl} onPick={pick} onPointed={onPointed} onUndo={onUndo} onIssue={onIssue} />
              ))}
            </ul>
          </div>
        ))
      )}

      {data && data.staleCount > 0 && (
        <div className="surface mt-4 flex items-center justify-between gap-3 px-4 py-3 text-sm text-[var(--color-muted-foreground)]">
          <span>
            {data.staleCount === 1
              ? "1 ancien prospect"
              : `${data.staleCount} anciens prospects`}{" "}
            : plus de 7 jours sans réponse
          </span>
          <Link
            to="/prospects?tab=anciens"
            className="shrink-0 font-semibold text-[var(--color-navy-700)] hover:underline"
          >
            Voir
          </Link>
        </div>
      )}
    </div>
  );

  return (
    <>
      <PageHeader
        title="Aujourd'hui"
        subtitle={`${todayLabel()} · heures chez toi, heure de Paris en dessous`}
      />

      <CallsStrip bookingUrl={bookingUrl} onPointed={onStripPointed} />

      {loading && !data ? (
        <div className="grid min-h-64 place-items-center">
          <Loader2 className="animate-spin text-[var(--color-navy-600)]" />
        </div>
      ) : failed && !data ? (
        <div className="surface flex flex-col items-center gap-3 px-6 py-10 text-center">
          <AlertTriangle className="text-[var(--color-warning)]" />
          <p className="text-sm">Impossible de charger ta journée.</p>
          <button type="button" className="btn btn-outline btn-sm" onClick={load}>
            Réessayer
          </button>
        </div>
      ) : wide ? (
        <div className="grid grid-cols-[minmax(0,1fr)_minmax(340px,400px)] items-start gap-4">
          {list}
          <div className="sticky top-6">
            {selected ? (
              <CallBrief
                fill
                prospect={withNotes(selected.prospect)}
                attendee={selected.attendee}
                event={selected.event}
                bookingUrl={bookingUrl}
                webinarHeldAt={
                  selected.attendee ? heldAtById.get(selected.attendee.webinarId) : null
                }
                onIssue={onIssue}
                onNotesSaved={onNotesSaved}
                hadAttendedRdv={selected.hadAttendedRdv}
                lastSaleProduct={selected.lastSaleProduct}
                callSummary={selected.callSummary}
              />
            ) : (
              <p className="surface px-6 py-10 text-center text-sm text-[var(--color-muted-foreground)]">
                Rien de prévu aujourd'hui.
              </p>
            )}
          </div>
        </div>
      ) : (
        list
      )}

      {issue && (
        <OutcomeModal
          open
          prospect={issue.prospect}
          eventId={issue.eventId}
          eventStart={issueEventStart}
          initialChoice={issue.choice}
          bookingUrl={bookingUrl}
          lastSaleProduct={issueLastSale}
          onClose={() => setIssue(null)}
          onDone={load}
        />
      )}
    </>
  );
}
